// @flow
import React from 'react';
import { intlShape, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { Button, buttonTypes, buttonSizes } from 'moods-react/Button';
import { IconUiCart, IconUiBin } from 'moods-react/Icons';
import Results from '../../results/ResultsComponent';
import DeleteDialog from '../../../common/dialog/delete/DeleteDialogComponent';
import { canUpdateProduct } from '../../../services/user-permission/UserPermissionService';
import messages from '../../../../data/products';
import AnalyticsClient from '../../../../clients/AnalyticsClient';
import productEvents from '../../../../analytics/events/products';

import './BulkActionsBar.scss';

const isSelectable = (product) => product.isChecked && !product.isDeleted;
const isChecked = (product) => product.isChecked;
const isExcluded = (product) => !product.isChecked && !product.isDeleted;
const isPurchasable = (product) => product.isChecked && !product.isDeleted && product.availability.isAvailable;

/**
 * Displays Bulk Actions Bar for products
 */
type Item = {
  id: string,
  isDeleted?: boolean,
  isChecked?: boolean,
  availability: {
    isAvailable: boolean,
  },
  endpoints: {
    downloadPdfUrl: string,
  },
};

type Actions = {
  cartRequest: Function,
  productDeselectAllAction: Function,
  productSettings: Function,
  couldNotAddToCart: Function,
  deleteRequest: Function,
  showOverlay: Function,
  closeOverlay: Function,
};

type Props = {
  items: [Item],
  totalItemCount: number,
  permissions: [string],
  isAdding?: boolean,
  isAdded?: boolean,
  isDeleting?: boolean,
  isDeleted?: boolean,
  isLoading?: boolean,
  selectAll?: boolean,
  actions: Actions,
};

class BulkActionsBarComponent extends React.Component<Props> {
  UNSAFE_componentWillReceiveProps(nextProps: Props): void {
    if (!this.props.isAdded && nextProps.isAdded) {
      setTimeout(() => {
        this.props.actions.productSettings({ isAdded: false });
      }, 2000);
    }
  }

  getSelectionIds(filterCriteria: { isPurchasable?: boolean, isSelectable?: boolean }): Array<string> {
    if (this.props.selectAll) {
      return [];
    }
    const selectedProducts = this.props.items.filter(filterCriteria);

    return selectedProducts.map((item) => item.id);
  }

  getExcludedIds(): Array<Item> {
    return this.props.selectAll ? this.props.items.filter(isExcluded).map((item) => item.id) : [];
  }

  cartClick = (): void => {
    if (!this.props.isAdding && !this.props.isAdded) {
      const purchasableIds = this.getSelectionIds(isPurchasable);
      const isSelectAll = this.props.selectAll;
      if (!isSelectAll && purchasableIds.length === 0) {
        const selectableIds = this.getSelectionIds(isSelectable);
        this.props.actions.couldNotAddToCart(selectableIds.length);
      } else {
        const excludedIds = this.getExcludedIds();
        this.props.actions.productSettings({ isAdding: true });
        this.props.actions.cartRequest({ ids: purchasableIds, excludedIds, isBulkMode: true });
      }
    }
  };

  deleteAction = (): void => {
    this.props.actions.productSettings({ isDeleting: true });
    this.props.actions.deleteRequest({
      ids: this.getSelectionIds(isChecked),
      excludedIds: this.getExcludedIds(),
      isBulkMode: true,
    });
  };

  deleteDialog = (): Element => {
    return (
      <DeleteDialog
        description={
          <FormattedMessage
            {...messages.deleteDialogButtonDescription}
            values={{
              checkedItemCount: this.getCheckedItemCount(),
              formattedCheckedItemCount: <strong>{this.getCheckedItemCount()}</strong>,
            }}
          />
        }
        actions={{
          deleteAction: this.deleteAction,
          closeOverlay: this.props.actions.closeOverlay,
        }}
      />
    );
  };

  deleteClick = (): void => {
    const deleteDialog = this.deleteDialog();
    this.props.actions.showOverlay({
      overlayName: DeleteDialog.displayName,
      overlayContent: deleteDialog,
    });
  };

  deselectAll = (): void => {
    this.props.actions.productDeselectAllAction();
    AnalyticsClient.trackEvent(productEvents.productDeselectAll());
  };

  renderBulkAddToCartButton(): Element {
    const { isAdding, isAdded } = this.props;
    const text = this.context.intl.formatMessage;
    const getLabel = () => {
      if (!isAdding && isAdded) {
        return text(messages.addedToCart);
      }
      if (isAdding && !isAdded) {
        return text(messages.addingToCart);
      }

      return text(messages.addToCartButton);
    };

    return (
      <Button
        isDisc
        icon={IconUiCart}
        size={buttonSizes.LARGE}
        onClick={this.cartClick}
        isLoading={isAdding}
        isSuccess={isAdded}
        label={getLabel()}
      />
    );
  }

  renderBulkDeleteButton(): Element | string {
    const { isDeleting, isDeleted, permissions } = this.props;

    if (!canUpdateProduct(permissions)) {
      return '';
    }

    const text = this.context.intl.formatMessage;
    const getLabel = () => {
      if (!isDeleting && isDeleted) {
        return text(messages.deletedProduct);
      }

      if (isDeleting && !isDeleted) {
        return text(messages.deletingProduct);
      }

      return text(messages.deleteProductButton);
    };

    return (
      <Button
        isDisc
        icon={IconUiBin}
        size={buttonSizes.LARGE}
        type={buttonTypes.STANDALONE_WHITE}
        onClick={this.deleteClick}
        isLoading={isDeleting}
        isSuccess={isDeleted}
        label={getLabel()}
      />
    );
  }

  getCheckedItemCount(): number {
    if (this.props.selectAll) {
      const excludedCount = this.props.items.filter(isExcluded).length;
      return this.props.totalItemCount - excludedCount;
    }
    return this.props.items.filter(isSelectable).length;
  }

  render(): Element {
    const checkedItemCount = this.getCheckedItemCount();
    const isVisible = checkedItemCount > 0 || this.props.isDeleted || this.props.isDeleting;
    const visibleClassName = isVisible ? ' toolbar-sliding--visible' : '';

    return (
      <div className={`toolbar-sliding${visibleClassName} bulk-actions-bar-component`}>
        <div className="bulk-actions-bar-component--left">
          <div className="bulk-actions-bar-component__container">{this.renderBulkAddToCartButton()}</div>
          <div className="bulk-actions-bar-component__container">{this.renderBulkDeleteButton()}</div>
        </div>
        <div className="bulk-actions-bar-component--right">
          <div className="bulk-actions-bar-component__container">
            <Results
              totalResults={this.props.totalItemCount}
              selectedResults={checkedItemCount}
              isLoading={this.props.isLoading}
            />
          </div>
          <div className="bulk-actions-bar-component__container">
            <Button
              type={buttonTypes.SECONDARY} // Deselect
              onClick={this.deselectAll}
            >
              {this.context.intl.formatMessage(messages.deselectAllProductsButton)}
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

BulkActionsBarComponent.displayName = 'BulkActionsBar';

BulkActionsBarComponent.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      isDeleted: PropTypes.bool,
      isChecked: PropTypes.bool,
      availability: PropTypes.shape({
        isAvailable: PropTypes.bool,
      }).isRequired,
      endpoints: PropTypes.shape({
        downloadPdfUrl: PropTypes.string,
      }).isRequired,
    }),
  ).isRequired,
  totalItemCount: PropTypes.number.isRequired,
  permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
  isAdding: PropTypes.bool,
  isAdded: PropTypes.bool,
  isDeleting: PropTypes.bool,
  isDeleted: PropTypes.bool,
  isLoading: PropTypes.bool,
  selectAll: PropTypes.bool,
  actions: PropTypes.shape({
    cartRequest: PropTypes.func.isRequired,
    productDeselectAllAction: PropTypes.func.isRequired,
    productSettings: PropTypes.func.isRequired,
    couldNotAddToCart: PropTypes.func.isRequired,
    deleteRequest: PropTypes.func.isRequired,
    showOverlay: PropTypes.func.isRequired,
    closeOverlay: PropTypes.func.isRequired,
  }).isRequired,
};

BulkActionsBarComponent.defaultProps = {
  isVisible: false,
  isAdding: false,
  isAdded: false,
  isDeleting: false,
  isDeleted: false,
  isLoading: false,
  selectAll: false,
};

BulkActionsBarComponent.contextTypes = {
  intl: intlShape.isRequired,
};

export default BulkActionsBarComponent;
